import Select from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";

import styled, { css } from "styled-components";

import { StyledActionDropdown } from "shared/components/ActionDropdown/styles";

import {
  borderRadius,
  colors,
  space,
  fontSize,
  transition,
  boxShadow,
  zIndex,
  iconSize,
  breakpoint,
  fontStyle,
} from "shared/styles/constants";
import { hoverFocus } from "shared/styles/defaults";
import { IconButton } from "shared/components/IconButton/IconButton";
import { ActionDropdownTrigger } from "shared/components/ActionDropdown/components/ActionDropdownTrigger";
import { rgba } from "polished";
import { StyledInputField } from "../styles";

export const StyledDropdown = styled.div`
  overflow: hidden;

  position: absolute;
  left: 0;
  right: 0;
  z-index: ${zIndex.dropdown};

  margin-top: ${space.xs};

  border-radius: ${borderRadius.m};
  box-shadow: ${boxShadow.l};
  background-color: ${colors.black};
`;

const ReactSelectStyles = css`
  && {
    .select {
      &__input {
        color: ${colors.black};

        input {
          box-shadow: none !important;
        }
      }

      &__control {
        position: relative;
        z-index: ${zIndex.above};

        cursor: pointer;

        flex-wrap: nowrap;
        min-height: fit-content;

        margin: ${space.s};
        padding: ${space.xxs} ${space.xs};
        border: none;
        border-radius: ${borderRadius.xs};

        background-color: ${colors.white};

        color: ${colors.black};
        ${fontSize.base}

        // prevent auto zoom on Safari
        ${breakpoint("s", "down")} {
          ${fontSize.l}
        }

        &--is-focused {
          ${hoverFocus.focusStyle};
        }
      }

      &__value-container {
        padding: 0;
      }

      &__single-value {
        color: ${colors.black};
      }

      &__menu {
        position: relative;

        margin: ${space.xxs} 0 0 0;
        background-color: transparent;
        border-radius: none;
        box-shadow: none;
      }

      &__menu-list {
        padding: 0;
      }

      &__option {
        cursor: pointer;

        color: ${colors.white};
        padding: ${space.xs} ${space.s};

        &--is-focused {
          background-color: ${colors.extraBlack} !important; // override selected style
        }

        &--is-selected {
          border-left: 2px solid ${colors.green};
          background-color: transparent;
        }

        &--is-disabled {
          opacity: 0.6;
          color: ${rgba(colors.grey6, 0.6)};
          cursor: not-allowed;
        }

        &--is-label {
          padding: ${space.s};
          margin: ${space.s} 0 ${space.xs};
          pointer-events: none;
          background-color: ${colors.grey2};
        }
      }
    }
  }
`;

export const StyledReactSelect = styled(Select)`
  ${ReactSelectStyles}
`;

export const StyledAsyncSelect = styled(AsyncSelect)`
  ${ReactSelectStyles}
`;

export const StyledCreatableSelect = styled(CreatableSelect)`
  ${ReactSelectStyles}
`;

export const StyledCreatableInputSelect = styled(CreatableSelect)`
  && {
    .multi-creatable {
      &__control {
        border: none;
        padding: ${space.xs} ${space.m} ${space.xs} ${space.xs};
        min-height: 42px;

        &--is-focused {
          ${hoverFocus.focusStyle};
        }

        &.has-error {
          box-shadow: 0 0 0 2px ${colors.red};
        }

        &--is-disabled {
          opacity: 0.6;
          color: ${rgba(colors.black, 0.6)};
          cursor: not-allowed;
        }
      }

      &__value-container {
        padding: 0;
        margin: -${space.xxs} 0;
      }

      &__multi-value {
        border-radius: 100px;
        padding: 2px;
        ${fontStyle.medium}

        &__remove {
          cursor: pointer;

          display: flex;
          align-items: center;
          justify-content: center;

          width: 20px;
          height: 20px;

          padding: 0;
          border-radius: 100px;

          &:hover,
          &:focus {
            background-color: ${colors.white};
          }
        }
      }
    }
  }
  .multi-creatable__indicator {
    padding: 0;
  }
`;

export const StyledMultiSelect = styled(Select)`
  && {
    .multi-select {
      &__control {
        border: none;
        padding: ${space.xs} ${space.m} ${space.xs} ${space.xs};
        min-height: 42px;

        &--is-focused {
          ${hoverFocus.focusStyle};
        }

        &.has-error {
          box-shadow: 0 0 0 2px ${colors.red};
        }

        &--is-disabled {
          opacity: 0.6;
          color: ${rgba(colors.black, 0.6)};
          cursor: not-allowed;
        }
      }

      &__value-container {
        padding: 0;
        margin: -${space.xxs} 0;
      }

      &__multi-value {
        border-radius: 100px;
        padding: 2px;
        ${fontStyle.medium}

        &__remove {
          cursor: pointer;

          display: flex;
          align-items: center;
          justify-content: center;

          width: 20px;
          height: 20px;

          padding: 0;
          border-radius: 100px;

          &:hover,
          &:focus {
            background-color: ${colors.white};
          }
        }
      }

      &__menu {
        margin-top: ${space.xs};

        border-radius: ${borderRadius.m};
        box-shadow: ${boxShadow.l};
        background-color: ${colors.black};
      }

      &__menu-list {
        padding: ${space.xxs} 0;
      }

      &__option {
        cursor: pointer;

        color: ${colors.white};

        &--is-focused {
          background-color: ${colors.extraBlack} !important; // override selected style
        }
      }
    }
  }
`;

export const StyledPillSelect = styled.input`
  ${StyledInputField}
`;

export const StyledValue = styled.div`
  display: flex;
  align-items: center;

  width: 100%;

  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  > * {
    flex: 0 0 auto;
  }

  > * + * {
    margin-left: ${space.xs};
  }
`;

interface StyledDropdownIndicatorProps {
  isOpen?: boolean;
}

export const StyledDropdownIndicator = styled.span<StyledDropdownIndicatorProps>`
  color: ${({ isOpen }) => (isOpen ? colors.red : "inherit")} !important;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-left: auto;

  svg {
    width: ${iconSize.s};
    height: ${iconSize.s};

    margin-left: ${space.xs};

    color: ${colors.grey4};

    transition: transform ${transition.default};
    transform: ${({ isOpen }) => (isOpen ? "rotate(180deg)" : "none")};
  }
`;

export const StyledPrefixIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const MultiSelectContainer = styled.div`
  color: ${colors.black};

  ${breakpoint("s", "down")} {
    ${fontSize.l}
  }
`;

export const IconDropdownContainer = styled(StyledActionDropdown)`
  padding: ${space.s};

  display: grid;
  grid-template-columns: repeat(15, 1fr);
  max-width: 100% !important;
  margin: 0 1rem;

  ${breakpoint("s", "down")} {
    grid-template-columns: repeat(10, 1fr);
    max-height: 200px;
    overflow-y: auto;
  }
`;

export const StyledIconItem = styled(IconButton)`
  margin: ${space.xxs};

  svg {
    color: ${colors.white};
    width: 24px !important;
    height: 24px !important;

    ${breakpoint("s", "down")} {
    }
  }

  ${breakpoint("s", "down")} {
    margin: 0;
  }
`;

export const IconSelectDropdownTrigger = styled(ActionDropdownTrigger)`
  height: 100%;
`;

export const IconSelectWrapper = styled.div`
  height: 100%;
`;

export const StyledOptionLabel = styled.span`
  min-width: 0;
  flex-shrink: 1;
  overflow: hidden;
`;
